import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useApolloClient, useMutation } from '@apollo/client'
import OPERATION_CREATE_RAN_ROOM, {
  OPERATION_DELETE_RAN_ROOM,
  OPERATION_RESET_RAN_ROOM,
  OPERATION_UPDATE_RAN_ROOM,
  OPERATION_IMPORT_RAN_ROOMS
} from 'pages/Ran/operations-gql/mutation'
import OPERATION_FIND_RAN_ROOMS, {
  OPERATION_FIND_RAN_ROOMS_REMAINING,
  RAN_ROOM_SSID_FRAGMENT
} from 'pages/Ran/operations-gql/queries'
import { PAGE_SIZE } from '../use-rans'
import useFilters, { useSort } from '../store'

const updateRemainingCount = (cache, variables, delta) => {
  cache.updateQuery({ query: OPERATION_FIND_RAN_ROOMS_REMAINING, variables }, oldData =>
    Number.isFinite(oldData?.count) ? { count: oldData.count + delta } : undefined
  )
}

const updateRemainingKeys = (cache, { siteId, ssidId, delta, includeTotal = true }) => {
  if (includeTotal) {
    updateRemainingCount(cache, { sf_id: siteId }, delta)
  }
  if (ssidId) {
    updateRemainingCount(cache, { sf_id: siteId, ssid_id: String(ssidId) }, delta)
  }
}

const readRoomSsidId = (cache, roomId) =>
  cache.readFragment({
    id: cache.identify({ __typename: 'RanRoom', id: parseInt(roomId) }),
    fragment: RAN_ROOM_SSID_FRAGMENT
  })?.ssid_id

const useActions = () => {
  const [processing, setProcessing] = useState()
  const { siteId } = useParams()
  const { cache } = useApolloClient()
  const [filters] = useFilters()
  const [sort] = useSort()
  const queryVariables = useMemo(() => {
    return {
      limit: PAGE_SIZE,
      sf_id: siteId,
      verbatim: filters.verbatim,
      created_start_at: filters.created_start_at ? filters.created_start_at.toUTCString() : '',
      created_end_at: filters.created_end_at ? filters.created_end_at.toUTCString() : '',
      sort_by: sort.by,
      sort_direction: sort.direction
    }
  }, [siteId, sort, filters])
  const [importRooms] = useMutation(OPERATION_IMPORT_RAN_ROOMS, {
    update: (cache, { data }) => {
      const { rooms } = data.result
      updateRemainingKeys(cache, { siteId, delta: -rooms.length })
      const importedBySsid = rooms.reduce(
        (counts, { ssid_id }) => (ssid_id ? { ...counts, [ssid_id]: (counts[ssid_id] ?? 0) + 1 } : counts),
        {}
      )
      Object.entries(importedBySsid).forEach(([ssidId, count]) => {
        updateRemainingKeys(cache, { siteId, ssidId, delta: -count, includeTotal: false })
      })
      cache.updateQuery(
        {
          query: OPERATION_FIND_RAN_ROOMS,
          variables: queryVariables
        },
        oldData => {
          return {
            rooms: [...rooms.map(room => ({ devices: [], ...room })), ...oldData.rooms]
          }
        }
      )
    }
  })
  const [resetRoom] = useMutation(OPERATION_RESET_RAN_ROOM)
  const [deleteRoom] = useMutation(OPERATION_DELETE_RAN_ROOM, {
    update: (cache, { data }) => {
      const ssidId = readRoomSsidId(cache, data.room.id)
      cache.modify({
        fields: {
          findRanRooms(existingRoomsRefs, { readField }) {
            return existingRoomsRefs.filter(ref => data.room.id !== readField('id', ref))
          }
        }
      })
      updateRemainingKeys(cache, { siteId, ssidId, delta: 1 })
    }
  })

  const [createRoom] = useMutation(OPERATION_CREATE_RAN_ROOM, {
    update: (cache, { data }) => {
      cache.updateQuery(
        {
          query: OPERATION_FIND_RAN_ROOMS,
          variables: queryVariables
        },
        oldData => {
          return { rooms: [{ devices: [], ...data.room }, ...oldData.rooms] }
        }
      )
      updateRemainingKeys(cache, { siteId, ssidId: data.room.ssid_id, delta: -1 })
    }
  })

  const [updateRoom] = useMutation(OPERATION_UPDATE_RAN_ROOM)

  return {
    processing,
    update: async ({ id, name, description, preSharedKeys }) => {
      setProcessing(true)
      const previousSsidId = readRoomSsidId(cache, id)
      try {
        const ran = await updateRoom({
          variables: {
            sf_id: siteId,
            room_id: parseInt(id),
            name,
            description,
            pre_shared_keys: preSharedKeys
          }
        })
        const nextSsidId = ran.data?.room?.ssid_id
        if (String(previousSsidId ?? '') !== String(nextSsidId ?? '')) {
          updateRemainingKeys(cache, { siteId, ssidId: previousSsidId, delta: 1, includeTotal: false })
          updateRemainingKeys(cache, { siteId, ssidId: nextSsidId, delta: -1, includeTotal: false })
        }
        return { ran }
      } catch (e) {
        return { error: e }
      } finally {
        setProcessing(false)
      }
    },
    create: async ({ name, description, preSharedKeys }) => {
      setProcessing(true)
      try {
        return {
          ran: await createRoom({
            variables: { sf_id: siteId, name, description, pre_shared_keys: preSharedKeys }
          })
        }
      } catch (e) {
        return { error: e }
      } finally {
        setProcessing(false)
      }
    },
    importRooms: async (rows, ssid_id) => {
      setProcessing(true)
      try {
        const {
          data: { result }
        } = await importRooms({
          variables: { sf_id: siteId, rows, ssid_id }
        })
        return {
          rooms: result.rooms,
          errors: result.errors
        }
      } catch (e) {
        console.log(e)
        return { errors: [e] }
      } finally {
        setProcessing(false)
      }
    },
    reset: async ({ roomId }) => {
      setProcessing(true)
      try {
        await resetRoom({
          variables: { sf_id: siteId, room_id: parseInt(roomId) }
        })
        return {}
      } catch (e) {
        return { error: e }
      } finally {
        setProcessing(false)
      }
    },
    delete: async ({ roomId }) => {
      setProcessing(true)
      try {
        await deleteRoom({
          variables: { sf_id: siteId, room_id: parseInt(roomId) }
        })
        return {}
      } catch (e) {
        return { error: e }
      } finally {
        setProcessing(false)
      }
    }
  }
}

export default useActions
